import React, { useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Button,
  Typography,
  CircularProgress,
  IconButton,
  Tabs,
  Tab,
  makeStyles,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import {
  buildForecastSheets,
  buildForecastWorkbook,
  buildForecastFilename,
  sheetToStyledHtml,
  downloadBlob,
} from "../forecastExcel";

const useStyles = makeStyles((theme) => ({
  paper: { borderRadius: 8, minWidth: 720, maxWidth: "90vw" },
  title: { fontWeight: 700, paddingRight: theme.spacing(5) },
  closeBtn: { position: "absolute", top: 8, right: 8, color: "#e53935" },
  previewBox: {
    overflow: "auto",
    maxHeight: "60vh",
    border: "1px solid #e0e0e0",
    padding: theme.spacing(1),
    backgroundColor: "#fafafa",
    "& table": { backgroundColor: "#fff" },
  },
  pillBtn: {
    backgroundColor: "#ec6535",
    color: "#fff",
    textTransform: "none",
    borderRadius: 9999,
    padding: "6px 22px",
    boxShadow: "none",
    "&:hover": { backgroundColor: "#9E4D27", boxShadow: "none" },
    "&.Mui-disabled": { backgroundColor: "#e0d2cb", color: "#fff" },
  },
  empty: { padding: 32, textAlign: "center", color: "#888" },
}));

export default function DownloadPreviewDialog({
  open,
  loading,
  rows,
  cmoLabel,
  brand,
  monthRange,
  syncId,
  columnHeader = "CMO",
  onClose,
}) {
  const classes = useStyles();
  const [tab, setTab] = React.useState(0);

  React.useEffect(() => {
    if (open) setTab(0);
  }, [open]);

  const sheets = useMemo(() => {
    if (!open || !monthRange) return null;
    return buildForecastSheets({
      rows: rows || [],
      cmoLabel,
      brand,
      monthRange,
      columnHeader,
    });
  }, [open, rows, cmoLabel, brand, monthRange, columnHeader]);

  const monthlyHtml = useMemo(
    () => (sheets ? sheetToStyledHtml(sheets.monthly) : ""),
    [sheets]
  );
  const legendHtml = useMemo(
    () => (sheets ? sheetToStyledHtml(sheets.legend) : ""),
    [sheets]
  );

  const onDownload = () => {
    const blob = buildForecastWorkbook({
      rows: rows || [],
      cmoLabel,
      brand,
      monthRange,
      columnHeader,
    });
    downloadBlob(
      blob,
      buildForecastFilename({ cmoLabel, brand, syncId: syncId || 0 })
    );
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      classes={{ paper: classes.paper }}
      maxWidth="lg"
    >
      <DialogTitle className={classes.title}>
        Download Monthly Report - Preview
        <IconButton size="small" className={classes.closeBtn} onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          indicatorColor="primary"
          textColor="primary"
        >
          <Tab label={sheets ? sheets.monthlyName : "Monthly Report"} />
          <Tab label="Legend" />
        </Tabs>
        <Box mt={2}>
          {loading ? (
            <Box className={classes.empty}>
              <CircularProgress size={24} />
            </Box>
          ) : !sheets || (rows || []).length === 0 ? (
            <Box className={classes.empty}>
              <Typography variant="body2">No data to preview.</Typography>
            </Box>
          ) : (
            <Box
              className={classes.previewBox}
              dangerouslySetInnerHTML={{
                __html: tab === 0 ? monthlyHtml : legendHtml,
              }}
            />
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} style={{ textTransform: "none" }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          className={classes.pillBtn}
          onClick={onDownload}
          disabled={loading || (rows || []).length === 0}
        >
          Download Excel
        </Button>
      </DialogActions>
    </Dialog>
  );
}
